/**
 * The Error Log's rows: one per uploaded file the server could not use - a
 * file its checks quarantined, the malware scan refused, or that was never
 * scanned clean (SCAN_FAILED: the scan could not finish, no verdict came
 * within 30 minutes of the upload, or the stored bytes were not the ones the
 * code was bound to - IP-92, IP-93) - with when it was uploaded and what was
 * wrong with it, in the words the drawer uses (uploadFile.js stateNotice).
 */
import { stateNotice } from './uploadFile.js';

const COLOMBO = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Colombo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});

/**
 * An instant in Asia/Colombo, as `DD/MM/YYYY HH:mm` and as a `YYYYMMDDHHmm`
 * key that sorts and compares as text.
 */
export function colomboDateTime(iso) {
  const at = new Date(iso);
  if (!iso || Number.isNaN(at.getTime())) return { dateTime: '-', sortKey: '' };
  const p = Object.fromEntries(COLOMBO.formatToParts(at).map((part) => [part.type, part.value]));
  return { dateTime: p.day + '/' + p.month + '/' + p.year + ' ' + p.hour + ':' + p.minute, sortKey: p.year + p.month + p.day + p.hour + p.minute };
}

/** One finding as a line: its row, then what is wrong (a file-level finding has no row). */
export function findingLine(f) {
  return (f.rowNo ? 'Row ' + f.rowNo + ': ' : '') + f.message;
}

/**
 * How many of a summary's findings are not among the `shown` - the server
 * keeps the first 100 in a batch's summary and lists the first 5 (findings.js
 * boundedSummary, sql.js LIST_SUMMARY_FINDINGS), and says how many there are
 * in all (`total`; a summary written before that has none, and holds all).
 */
export function unshownFindings(summary, shown) {
  const total = Number.isInteger(summary?.total) ? summary.total : (summary?.findings?.length ?? 0);
  return Math.max(0, total - shown);
}

/** A batch summary's findings, a line each, and how many more there are. */
function findingLines(batch) {
  const findings = batch.summary?.findings ?? [];
  const more = unshownFindings(batch.summary, findings.length);
  return [...findings.map(findingLine), ...(more ? ['... and ' + more + ' more: open the file to see every row.'] : [])];
}

/** Why a batch failed, or null for one that did not. */
export function errorOf(batch) {
  if (batch.state === 'REJECTED_MALWARE') {
    return { title: 'Malware detected in ' + batch.fileName, description: stateNotice(batch).text };
  }
  if (batch.state === 'SCAN_FAILED') {
    // FINGERPRINT_MISMATCH carries its finding in the summary; the rest say why in stateNotice's words.
    return {
      title: 'Scan failed for ' + batch.fileName + (batch.scanStatus ? ' (' + batch.scanStatus + ')' : ''),
      description: [stateNotice(batch).text, ...findingLines(batch)].join('\n'),
    };
  }
  if (batch.state !== 'QUARANTINED') return null;
  const findings = findingLines(batch);
  return {
    title: 'Quarantined: ' + batch.fileName,
    description: findings.length ? findings.join('\n') : (batch.errorCount ?? 0) + ' error(s) were found in this file.',
  };
}

/** @param {object[]} batches mapBatch's shape, as intake/batches lists them */
export function toErrorRows(batches) {
  return batches.flatMap((batch) => {
    const error = errorOf(batch);
    if (!error) return [];
    const uploadedAt = colomboDateTime(batch.createdAt);
    return [{ key: batch.batchId, timestamp: uploadedAt.dateTime, sortKey: uploadedAt.sortKey, message: error.title, description: error.description }];
  });
}
